import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { useDeleteFolder } from '@/api/community-essentials/delete-folder'
import { Checkbox } from '@/components/ui/checkbox'
import { ConfirmationPopup } from '@/components/ui/confirmation-popup'
import { Typography } from '@/components/ui/typography'
import { toastFactory } from '@/factories'
import type { CommunityEssentialFolderModel } from '@/models'
import { getApiErrorMessage } from '@/utils/error'

import * as S from './delete-folder-dialog.styled'

interface DeleteFolderDialogProps {
  folder: CommunityEssentialFolderModel
  onClose: () => void
  onDeleted: () => void
}

export const DeleteFolderDialog = ({ folder, onClose, onDeleted }: DeleteFolderDialogProps) => {
  const { t } = useTranslation(['community-essentials', 'common'])
  const [deleteContent, setDeleteContent] = useState(true)
  const { mutateAsync: deleteFolderMutation } = useDeleteFolder()

  const handleConfirm = async () => {
    try {
      await deleteFolderMutation({
        deleteContent,
        folderId: folder.id,
      })
      onDeleted()
    } catch (error) {
      toastFactory.error(getApiErrorMessage(error as Error))
    }
  }

  return (
    <ConfirmationPopup
      variant="danger"
      title={t('community-essentials:deleteFolderDialog.title')}
      confirmText={t('community-essentials:deleteFolderDialog.confirmText')}
      cancelText={t('common:actions.cancel')}
      onClose={onClose}
      onConfirm={handleConfirm}
    >
      <Typography size="text-medium">
        <Trans
          t={t}
          i18nKey="community-essentials:deleteFolderDialog.description"
          values={{ folderName: folder.name }}
          components={[<strong key="folder-name" />]}
        />
      </Typography>

      <S.CheckboxRow>
        <Checkbox
          checked={deleteContent}
          label={t('community-essentials:deleteFolderDialog.checkboxLabel')}
          onChange={(e) => setDeleteContent(e.target.checked)}
        />
      </S.CheckboxRow>
    </ConfirmationPopup>
  )
}
